import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { attachmentsQueries } from "./attachments-keys";
import type { AttachmentsByMessage } from "../attachments.types";

/**
 * Hook to fetch attachments for a list of messages
 * Uses TanStack Query with type-safe query key factory
 */
export function useAttachmentsByRoomQuery(roomId: string, messageIds: string[]) {
  const queryClient = useQueryClient();

  const query = useQuery({
    ...attachmentsQueries.byRoom(roomId, messageIds),
    enabled: !!roomId,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000, // 30 seconds - messages change frequently
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!roomId || !messageIds.length) return;

    const cached =
      queryClient.getQueryData<AttachmentsByMessage>(
        attachmentsQueries.byRoom(roomId, []).queryKey,
      ) ?? query.data;

    const hasMissingMessageIds = messageIds.some((messageId) => cached?.[messageId] === undefined);

    if (hasMissingMessageIds && !query.isFetching) {
      void query.refetch();
    }
  }, [messageIds, query, queryClient, roomId]);

  return query;
}
